import opentype from "opentype.js";
import { hashString } from "../utils/common";

export default class FontLoader {
  private static tasks: Map<string, Promise<opentype.Font | undefined>> =
    new Map();
  private static openTypeFonts: Map<string, opentype.Font> = new Map();

  static async loadOpenTypeFont(url: string) {
    try {
      const fontFamily = `font-${await hashString(url, 16)}`;
      const font = this.openTypeFonts.get(fontFamily);
      if (font) return font;

      const currentTask = this.tasks.get(fontFamily);
      if (currentTask) return currentTask;

      const newTask = opentype
        .load(url)
        .then((font) => {
          this.openTypeFonts.set(fontFamily, font);
          return font;
        })
        .catch((error) => {
          console.error("Failed to load font:", error);
          return undefined;
        })
        .finally(() => this.tasks.delete(fontFamily));

      this.tasks.set(fontFamily, newTask);
      return newTask;
    } catch (error) {
      console.error("Failed to load font:", error);
    }
  }

  static async loadFontFace(
    url: string,
    async: boolean = true,
  ): Promise<FontFace | undefined> {
    try {
      const fontFamily = `font-${await hashString(url, 16)}`;
      const loadedFontFace = Array.from(document.fonts).find(
        (font) => font.family === fontFamily,
      );
      if (loadedFontFace) return loadedFontFace;

      const newFontFace = new FontFace(fontFamily, `url(${url})`);
      if (async) {
        const loadedFontFace = await newFontFace.load();
        document.fonts.add(loadedFontFace);
        return loadedFontFace;
      }

      newFontFace.load().then((loadedFontFace) => {
        document.fonts.add(loadedFontFace);
      });
      return newFontFace;
    } catch (error) {
      console.error("Failed to load font:", error);
    }
  }

  static getFontAscenderAndDescender(font: opentype.Font) {
    let ascender = 0;
    let descender = 0;

    if (
      font.tables?.os2 &&
      font.tables.os2.usWinAscent &&
      font.tables.os2.usWinDescent
    ) {
      ascender = font.tables.os2.usWinAscent;
      descender = -font.tables.os2.usWinDescent;
    } else if (font.tables?.head) {
      ascender = font.tables.head.yMax;
      descender = font.tables.head.yMin;
    } else {
      ascender = font.ascender;
      descender = font.descender;
    }

    return { ascender: ascender || 0, descender: descender || 0 };
  }
}
